/**
 * Edit Activity Modal
 *
 * Modal for editing an existing activity (call, meeting, task, note).
 * Updates the Activity record when saved.
 */

import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { activityStorage } from '../../services/activityStorage';
import { Activity, CallOutcome, CallDirection } from '../../types/activity';
import { useToast } from '../../contexts/ToastContext';

interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: Activity | null;
  onActivityUpdated?: () => void;
}

const callOutcomes: { value: CallOutcome; label: string }[] = [
  { value: 'connected', label: 'Connected' },
  { value: 'voicemail', label: 'Left Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'wrong_number', label: 'Wrong Number' },
];

export const EditActivityModal: React.FC<EditActivityModalProps> = ({
  isOpen,
  onClose,
  activity,
  onActivityUpdated,
}) => {
  const { success } = useToast();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [direction, setDirection] = useState<CallDirection>('outbound');
  const [outcome, setOutcome] = useState<CallOutcome | ''>('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');

  // Populate form when activity changes
  useEffect(() => {
    if (activity) {
      setSubject(activity.subject || '');
      setDescription(activity.description || '');
      setDuration(activity.duration?.toString() || '');
      setDirection(activity.direction || 'outbound');
      setOutcome(activity.outcome || '');
      setLocation(activity.location || '');
      setError('');

      // Parse scheduledAt for meetings
      if (activity.scheduledAt) {
        const scheduled = new Date(activity.scheduledAt);
        setMeetingDate(scheduled.toISOString().split('T')[0]);
        setMeetingTime(scheduled.toTimeString().slice(0, 5));
      } else {
        setMeetingDate('');
        setMeetingTime('');
      }
    }
  }, [activity]);

  if (!isOpen || !activity) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Update activity
    const updates: Partial<Activity> = {
      subject: subject || undefined,
      description: description || undefined,
      duration: duration ? parseInt(duration, 10) : undefined,
    };

    // Add call-specific fields
    if (activity.type === 'call') {
      updates.direction = direction;
      updates.outcome = outcome as CallOutcome || undefined;
    }

    // Add meeting-specific fields
    if (activity.type === 'meeting') {
      updates.location = location || undefined;
      if (meetingDate && meetingTime) {
        updates.scheduledAt = `${meetingDate}T${meetingTime}:00`;
        // Keep existing timezone or use current user's timezone
        updates.timezone = activity.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
    }

    try {
      await activityStorage.update(activity.id, updates);

      success('Activity updated', `${activity.type.charAt(0).toUpperCase() + activity.type.slice(1)} has been updated`);

      onActivityUpdated?.();
      onClose();
    } catch (err) {
      console.error('[EditActivityModal] Error updating activity:', err);
      setError('Failed to update activity. Please try again.');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const getTitle = () => {
    const typeLabel = activity.type.charAt(0).toUpperCase() + activity.type.slice(1);
    return `Edit ${typeLabel}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {getTitle()}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Subject (not for notes) */}
            {activity.type !== 'note' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject..."
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Call-specific fields */}
            {activity.type === 'call' && (
              <>
                {/* Direction */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Direction
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="direction"
                        value="outbound"
                        checked={direction === 'outbound'}
                        onChange={(e) => setDirection(e.target.value as CallDirection)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300">Outbound</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="direction"
                        value="inbound"
                        checked={direction === 'inbound'}
                        onChange={(e) => setDirection(e.target.value as CallDirection)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-gray-700 dark:text-gray-300">Inbound</span>
                    </label>
                  </div>
                </div>

                {/* Outcome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Call Outcome
                  </label>
                  <select
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value as CallOutcome)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select outcome...</option>
                    {callOutcomes.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Meeting-specific fields */}
            {activity.type === 'meeting' && (
              <>
                {/* Date & Time Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Time
                    </label>
                    <input
                      type="time"
                      value={meetingTime}
                      onChange={(e) => setMeetingTime(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Zoom, Conference Room A"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {/* Duration (for calls and meetings) */}
            {(activity.type === 'call' || activity.type === 'meeting') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g., 15"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {/* Description/Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {activity.type === 'note' ? 'Note' : 'Notes'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder={activity.type === 'note' ? 'Enter your note...' : 'Additional notes...'}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
